import { useEffect, useState } from "react";
import { Bell, BellRing, CheckCircle2, BellOff } from "lucide-react";

type FollowState = "unsupported" | "idle" | "following" | "blocked" | "asking";

const STORAGE_KEY = "lww-follow-notifications";

export function FollowNotifications() {
  const [state, setState] = useState<FollowState>("idle");

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setState("unsupported");
      return;
    }
    const permission = Notification.permission;
    if (permission === "denied") {
      setState("blocked");
      return;
    }
    if (permission === "granted" && localStorage.getItem(STORAGE_KEY) === "on") {
      setState("following");
      return;
    }
    setState("idle");
  }, []);

  const onFollow = async () => {
    if (!("Notification" in window)) {
      setState("unsupported");
      return;
    }
    setState("asking");
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        localStorage.setItem(STORAGE_KEY, "on");
        setState("following");
        try {
          new Notification("You're following LifeWestWay", {
            body: "We'll let you know when new visa, tax and relocation guides go live.",
            icon: "/favicon.ico",
            tag: "lww-follow-welcome",
          });
        } catch {
          /* notification display is best-effort */
        }
      } else if (permission === "denied") {
        setState("blocked");
      } else {
        setState("idle");
      }
    } catch {
      setState("idle");
    }
  };

  const onUnfollow = () => {
    localStorage.removeItem(STORAGE_KEY);
    setState("idle");
  };

  return (
    <section
      aria-label="Follow LifeWestWay for browser notifications"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-navy via-[#0d2544] to-navy p-6 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)] sm:p-8"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-accent/20 blur-3xl"
      />
      <div className="relative grid gap-5 md:grid-cols-[1.2fr_1fr] md:items-center">
        <div>
          <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-accent">
            <Bell aria-hidden="true" className="h-3.5 w-3.5" /> Follow LifeWestWay
          </p>
          <h3 className="mt-2 font-display text-2xl font-bold text-white sm:text-3xl">
            Stay updated — no email required.
          </h3>
          <p className="mt-2 text-sm text-white/70">
            Follow our site to receive instant browser notifications when new immigration, visa or
            tax guides are published. Entirely optional, and you can turn it off any time from your
            browser settings.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {state === "following" ? (
            <>
              <div className="inline-flex items-center justify-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-5 py-2.5 text-sm font-semibold text-accent">
                <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                You&apos;re following
              </div>
              <button
                type="button"
                onClick={onUnfollow}
                className="text-xs text-white/60 underline underline-offset-4 transition hover:text-white"
              >
                Stop notifications on this device
              </button>
            </>
          ) : state === "blocked" ? (
            <p className="rounded-2xl border border-white/15 bg-card/5 px-4 py-3 text-xs text-white/70">
              <BellOff aria-hidden="true" className="mr-1.5 inline h-3.5 w-3.5" />
              Notifications are blocked for this site in your browser. You can re-enable them from
              your browser&apos;s site settings.
            </p>
          ) : state === "unsupported" ? (
            <p className="rounded-2xl border border-white/15 bg-card/5 px-4 py-3 text-xs text-white/70">
              Your browser doesn&apos;t support notifications. You can still bookmark this page to
              check back for new guides.
            </p>
          ) : (
            <>
              <button
                type="button"
                onClick={onFollow}
                disabled={state === "asking"}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-foreground transition hover:brightness-110 disabled:opacity-70"
              >
                <BellRing aria-hidden="true" className="h-4 w-4" />
                {state === "asking" ? "Waiting for your choice…" : "Follow / Turn on notifications"}
              </button>
              <p className="text-xs text-white/50">
                Your browser will ask for permission. No email, no personal details.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default FollowNotifications;
